import { MAX_LENGTH, MAX_LENGTH_SUPPORT_TEXT } from "../../Constant";
import { dateFunc } from "../../utils/dateFunction";
import {
  convertHtmlToText,
  isHTML,
  isNumeric,
  isValidEmail,
  isValidUrlWithWWW,
  htmlToTextWithEnter,
  requiredValidationForFields,
} from "../../utils/Validator";
import {
  ErrorListType,
  productAPIDataType,
  productSetupFormDataType,
  SkuDetailsType,
} from "./ProductDataType";
import { RICH_TEXT_EDITOR_FIELD_NAMES } from "../../Constant";

export const mapProductAPIDataWithFormFields = (
  productAPIData: productAPIDataType | null,
) => {
  if (
    !productAPIData ||
    Object.keys(productAPIData).length === 0 ||
    productAPIData.ProductId === null
  ) {
    return null;
  }
  // productOwner, productManager, proxyOwner
  const {
    ProductId,
    Name,
    Description,
    CommercialDescription,
    AssetAdvice,
    ServLineDomainOwner,
    GlobalServiceCodesPrimary,
    GlobalServiceCodesSecondary,
    DemandAdviceContact,
    ProductOwner,
    ProxyOwner,
    ProductManager,
    ProductSupport,
    ProductSupportText,
  } = productAPIData;

  const productFormData: productSetupFormDataType = {
    empireCode: ProductId,
    productName: Name || "",
    productDescription: Description || "",
    commercialDescription: CommercialDescription || "",
    assetAdvice: AssetAdvice || "",
    ctPrortfolio: ServLineDomainOwner || "",
    primaryList: GlobalServiceCodesPrimary || "",
    secondryList: GlobalServiceCodesSecondary || "",
    commercialOwnerName: ProductOwner[0]?.Email || "",
    commercialOwnerProxyName: ProxyOwner[0]?.Email || "",
    ctTechnicalProductManager: ProductManager[0]?.Email || "",
    managementTeamID: DemandAdviceContact || "",
    teamEmailID: "EY_CTDemandMgmt_DMA.GID@ey.net",
    productContact: "EY_CTDemandMgmt_DMA.GID@ey.net",
    productSupport: ProductSupport || "",
    productSupportText: ProductSupportText || "",
  };
  return productFormData;
};

export const mapSkuListAPIDataWithFormFields = (
  skuListAPIData: SkuDetailsType[],
) => {
  if (!skuListAPIData || skuListAPIData.length === 0) {
    return null;
  }
  const skuListFormData = skuListAPIData.map((sku) => {
    const {
      SkuId,
      Name,
      CreatedDtm,
      UpdatedDtm,
      CreatedUser,
      StatusCd,
      StatusName,
      RejectedMessage,
      IsSkuApiKeyAvailable,
      UpdateStatusCd,
    } = sku;
    const serialNumber = skuListAPIData.indexOf(sku) + 1;
    const skuFormData = {
      serialNumber: serialNumber + ".",
      sku: SkuId,
      skuName: Name,
      createdBy: CreatedUser,
      createdDate: dateFunc(CreatedDtm).formattedDateOnly,
      updatedDate: dateFunc(UpdatedDtm).formattedDateOnly,
      status: StatusName,
      statusName: StatusName,
      statusCd: StatusCd || "",
      costMetricCd: sku.CostMetricCd || "",
      discription: sku.Description || "",
      skuId: SkuId,
      rejectedMessage: RejectedMessage || "",
      IsSkuApiKeyAvailable: IsSkuApiKeyAvailable,
      updateStatusCd: UpdateStatusCd,
    };
    return skuFormData;
  });
  return skuListFormData;
};

const requiredValidation = (value: string) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (isHTML(value)) {
    value = convertHtmlToText(value);
  }
  if (value === "") {
    return false;
  }
  return true;
};

const checkMaxLength = (value: string) => {
  const maxLengthLimit = MAX_LENGTH;
  return value.length <= maxLengthLimit;
};

const strReplace = (str: string, search: string, replace: string) => {
  const regex = new RegExp(search, "g");
  return str.replace(regex, replace);
};

export const validateFormFields = (
  name: string,
  value: string,
  errorList: ErrorListType,
  setError: React.Dispatch<React.SetStateAction<ErrorListType>>,
) => {
  let isError = false;
  const errorObject = errorList[name as keyof typeof errorList];
  if (errorObject === undefined) {
    return false;
  }
  const validations = errorObject?.validation;
  const label = strReplace(errorObject?.label, ":", " ");
  if (validations.length === 0) {
    return false;
  }
  if (validations.includes("required")) {
    let isValid = false;
    let hasHtml = false;
    if (RICH_TEXT_EDITOR_FIELD_NAMES.includes(name)) {
      isValid = requiredValidation(value);
    } else {
      ({ isValid, hasHtml } = requiredValidationForFields(value));
    }

    if (isValid) {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name]: {
          ...prevState[name as keyof typeof errorList],
          isError: false,
          message: "",
        },
      })); // set error to false
    } else {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name]: {
          ...prevState[name as keyof typeof errorList],
          isError: true,
          message: !hasHtml
            ? `${label} is a required field`
            : `${label} contains invalid characters`,
        },
      })); // set error to true
      isError = true;
    }
  }
  if (!isError && validations.includes("isNumber")) {
    if (value === null || value === "" || isNumeric(value)) {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: false,
          message: "",
        },
      })); // set error to false
    } else {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: true,
          message: `${label} should be a number`,
        },
      })); // set error to true
      isError = true;
    }
  }
  if (!isError && validations && validations.includes("maxLength")) {
    if (isHTML(value)) {
      value = htmlToTextWithEnter(value);
    }

    let isValid;
    if (
      name == "productSupportText" &&
      value.length > MAX_LENGTH_SUPPORT_TEXT
    ) {
      isValid = false;
    } else {
      // assetAdvice and commercialDescription - rich text editor fields
      isValid = checkMaxLength(value);
    }

    if (isValid) {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: false,
          message: "",
        },
      })); // set error to false
    } else {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: true,
          message: `${label} should not exceed ${
            name === "productSupportText" ? MAX_LENGTH_SUPPORT_TEXT : MAX_LENGTH
          } characters`,
        },
      })); // set error to true
      isError = true;
    }
  }
  if (!isError && validations && validations.includes("isEmailOrUrl")) {
    if (
      value === null ||
      value === "" ||
      isValidEmail(value) ||
      isValidUrlWithWWW(value)
    ) {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: false,
          message: "",
        },
      })); // set error to false
    } else {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: true,
          message: `${label} should be a valid email or url`,
        },
      })); // set error to true
      isError = true;
    }
  }
  if (!isError && validations && validations.includes("isUrl")) {
    if (value === null || value === "" || isValidUrlWithWWW(value)) {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: false,
          message: "",
        },
      })); // set error to false
    } else {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof ErrorListType],
          isError: true,
          message: `${label} should be a valid url`,
        },
      })); // set error to true
      isError = true;
    }
  }

  if (!isError && validations && validations.includes("richTextEmptyCheck")) {
    if (isHTML(value)) {
      value = convertHtmlToText(value);
    }
    const validValue = value.trim();
    if (validValue.length > 0) {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name]: {
          ...prevState[name as keyof typeof errorList],
          isError: false,
          message: "",
        },
      })); // set error to false
    } else {
      setError((prevState: ErrorListType) => ({
        ...prevState,
        [name]: {
          ...prevState[name as keyof typeof errorList],
          isError: true,
          message: `${label} is a required field`,
        },
      })); // set error to true
      isError = true;
    }
  }
  return isError;
};
